import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, Shield, Loader2, Save, Image, Type, BarChart3, 
  MessageSquare, Phone, Mail, Instagram, Twitter, Youtube,
  Facebook, Plus, Trash2, GripVertical, Eye, ChevronDown, ChevronUp,
  Sparkles, Music, Users, Award, ExternalLink, CheckCircle, HardDrive,
  Video, X, FileText, Globe, Crown, Building2, Handshake, Gift, Ticket,
  Star, Heart, Zap, TrendingUp, Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { settingsApi, adminRepairsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import CloudinaryUploader from '../../components/CloudinaryUploader';

// Icon mapping
const iconMap = {
  sparkles: Sparkles,
  music: Music,
  users: Users,
  award: Award,
};

const socialIcons = {
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  facebook: Facebook,
  linkedin: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  whatsapp: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  ),
  email: Mail,
  tiktok: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
};

// Video Upload Field using CloudinaryUploader
const VideoUploadField = ({ value, onChange }) => {
  const clearVideo = () => onChange('');

  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
        Video de Fondo
      </Label>
      
      {value ? (
        <div className="space-y-3">
          <div className="relative aspect-video max-w-md overflow-hidden rounded-xl border border-[#27272A]">
            <video 
              src={value}
              className="w-full h-full object-cover"
              muted
              loop
              autoPlay
              playsInline
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <button
              onClick={clearVideo}
              className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
            >
              <X size={16} className="text-white" />
            </button>
            <div className="absolute bottom-2 left-2 flex items-center gap-2">
              <CheckCircle size={14} className="text-green-400" />
              <span className="text-xs text-white">Video cargado</span>
            </div>
          </div>
          <CloudinaryUploader
            folder="bravo_videos"
            resourceType="video"
            accept="video/*"
            maxSize={100 * 1024 * 1024}
            buttonText="Cambiar video"
            showPreview={false}
            onUploadComplete={(result) => onChange(result.url)}
            data-testid="video-uploader-change"
          />
        </div>
      ) : (
        <CloudinaryUploader
          folder="bravo_videos"
          resourceType="video"
          accept="video/*"
          maxSize={100 * 1024 * 1024}
          buttonText="Subir Video"
          showPreview={false}
          onUploadComplete={(result) => onChange(result.url)}
          data-testid="video-uploader"
        />
      )}

      <p className="text-xs text-[#52525B] mt-3">
        MP4, MOV, WebM • Máx 100MB. El video se reproducirá automáticamente de fondo en el hero.
      </p>
    </div>
  );
};

const SettingsPage = () => {
  const { isOwner } = useAuth();
  const { refreshSettings } = useSiteSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [resendingDj, setResendingDj] = useState(false);
  const [resendDjResult, setResendDjResult] = useState(null);

  const handleResendDjEmails = async () => {
    setResendingDj(true);
    setResendDjResult(null);
    try {
      const res = await adminRepairsApi.resendDjApprovalEmails();
      setResendDjResult(res.data);
      toast.success('Reenvío completado');
    } catch (e) {
      toast.error('Error al reenviar emails');
    } finally {
      setResendingDj(false);
    }
  };

  const handleRepairTickets = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const res = await adminRepairsApi.relinkTickets();
      setRepairResult(res.data);
      toast.success('Reparación completada');
    } catch (e) {
      toast.error('Error al ejecutar la reparación');
    } finally {
      setRepairing(false);
    }
  };
  const [activeSection, setActiveSection] = useState('hero');
  const [settings, setSettings] = useState({
    hero: {
      subtitle: '',
      description: '',
      cta_primary_text: '',
      cta_primary_link: '',
      cta_secondary_text: '',
      cta_secondary_link: '',
      background_image: '',
      background_video: '',
      social_links: []
    },
    stats: {
      enabled: true,
      items: []
    },
    cta: {
      enabled: true,
      title: '',
      description: '',
      button_text: '',
      button_link: ''
    },
    footer: {
      contact_email: '',
      contact_phone: '',
      copyright_text: '',
      social_links: []
    },
    gallery: {
      drive_folder_id: '',
      title: 'Galería',
      description: 'Revive los mejores momentos de nuestros eventos',
      premium_download_price: 550
    },
    fullbravx: {
      enabled: true,
      badge: 'Comunidad VIP',
      title: 'FULL BRAVX',
      description: '',
      benefits: [],
      cta_text: 'Unirme a FULL BRAVX',
      cta_link: '/fullbravx',
      cta_subtitle: 'Probá 15 días gratis • Cancelá cuando quieras'
    },
    productoras: {
      enabled: true,
      badge: 'Para Productoras',
      title: 'Tu plataforma para',
      title_highlight: 'crear eventos épicos',
      description: '',
      features: [],
      cta_text: 'Quiero ser productora',
      cta_link: '/productoras/aplicar',
      stats: []
    },
    partners: {
      enabled: true,
      badge: 'Para Partners',
      title: 'Conectá tu negocio con',
      title_highlight: 'nuestra comunidad',
      description: '',
      features: [],
      cta_text: 'Quiero ser partner',
      cta_link: '/quiero-ser-partner',
      example_partners: [],
      stats_label: '+50 Partners',
      stats_sublabel: 'activos en la red'
    }
  });

  useEffect(() => {
    const fetchSettings = async () => {
      if (!isOwner) return;
      
      try {
        const response = await settingsApi.getSite();
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Error al cargar configuración');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [isOwner]);

  const handleChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleStatChange = (index, field, value) => {
    setSettings(prev => {
      const newItems = [...prev.stats.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return {
        ...prev,
        stats: { ...prev.stats, items: newItems }
      };
    });
  };

  const addStat = () => {
    setSettings(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        items: [...prev.stats.items, { value: '0+', label: 'Nuevo', icon: 'sparkles' }]
      }
    }));
  };

  const removeStat = (index) => {
    setSettings(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        items: prev.stats.items.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSocialChange = (section, index, field, value) => {
    setSettings(prev => {
      const newLinks = [...(prev[section].social_links || [])];
      newLinks[index] = { ...newLinks[index], [field]: value };
      return {
        ...prev,
        [section]: { ...prev[section], social_links: newLinks }
      };
    });
  };

  const addSocialLink = (section) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        social_links: [...(prev[section].social_links || []), { platform: 'instagram', url: '', enabled: true }]
      }
    }));
  };

  const removeSocialLink = (section, index) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        social_links: prev[section].social_links.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateSite(settings);
      // Refresh the global site settings context so public pages update immediately
      await refreshSettings();
      toast.success(
        <div className="flex items-center gap-2">
          <CheckCircle size={18} className="text-green-500" />
          <span>¡Cambios guardados y publicados!</span>
        </div>,
        { duration: 4000 }
      );
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'hero', label: 'Hero Principal', icon: Image },
    { id: 'fullbravx', label: 'FULL BRAVX', icon: Crown },
    { id: 'productoras', label: 'Productoras', icon: Building2 },
    { id: 'partners', label: 'Partners', icon: Handshake },
    { id: 'footer', label: 'Footer', icon: Type },
    { id: 'gallery', label: 'Galería', icon: HardDrive },
    { id: 'mantenimiento', label: 'Mantenimiento', icon: Wrench },
  ];

  if (!isOwner) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="p-6 lg:p-8">
            <div className="admin-card text-center py-16">
              <Shield size={48} className="mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="text-xl font-black uppercase mb-4">Acceso Restringido</h3>
              <p className="text-[#A1A1AA]">
                Solo el administrador principal puede modificar la configuración del sitio.
              </p>
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 space-y-6"
          data-testid="settings-page"
        >
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-black tracking-tight uppercase flex items-center gap-3">
                  <Globe className="text-[#D4AF37]" size={28} />
                  Configuración del Sitio
                </h1>
                <p className="text-zinc-500 text-sm mt-1">
                  Personaliza el contenido de la página pública
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Link to="/admin/terminos">
                  <Button
                    variant="outline"
                    className="admin-admin-btn-secondary"
                  >
                    <FileText size={18} className="mr-2" />
                    Editar Términos
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={() => window.open('/', '_blank')}
                  className="admin-admin-btn-secondary"
                >
                  <Eye size={18} className="mr-2" />
                  Ver Sitio
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="admin-admin-btn-primary"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="grid gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="admin-card h-20 skeleton" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-12 gap-6">
                {/* Sidebar Navigation */}
                <div className="col-span-12 lg:col-span-3">
                  <div className="admin-card sticky top-8">
                    <h3 className="text-xs uppercase tracking-widest text-[#52525B] mb-4">Secciones</h3>
                    <nav className="space-y-1">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => setActiveSection(section.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-left transition-colors ${
                            activeSection === section.id
                              ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                              : 'text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]'
                          }`}
                        >
                          <section.icon size={18} />
                          <span className="text-sm font-medium">{section.label}</span>
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>

                {/* Content Area */}
                <div className="col-span-12 lg:col-span-9">
                  <AnimatePresence mode="wait">
                    {/* HERO Section */}
                    {activeSection === 'hero' && (
                      <motion.div
                        key="hero"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="admin-card">
                          <h2 className="text-xl font-black uppercase tracking-tight mb-6 flex items-center gap-2">
                            <Image size={20} className="text-[#D4AF37]" />
                            Hero Principal
                          </h2>
                          
                          <div className="space-y-6">
                            <div>
                              <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                Subtítulo
                              </Label>
                              <input
                                value={settings.hero.subtitle}
                                onChange={(e) => handleChange('hero', 'subtitle', e.target.value)}
                                placeholder="Productora de Eventos"
                                className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                              />
                            </div>

                            <div>
                              <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                Descripción
                              </Label>
                              <textarea
                                value={settings.hero.description}
                                onChange={(e) => handleChange('hero', 'description', e.target.value)}
                                placeholder="Descripción del hero..."
                                rows={3}
                                className="w-full bg-transparent border border-[#27272A] focus:border-white p-4 text-white placeholder:text-[#52525B] outline-none transition-colors resize-none"
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  Botón Principal - Texto
                                </Label>
                                <input
                                  value={settings.hero.cta_primary_text}
                                  onChange={(e) => handleChange('hero', 'cta_primary_text', e.target.value)}
                                  placeholder="Ver Eventos"
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  Botón Principal - Link
                                </Label>
                                <input
                                  value={settings.hero.cta_primary_link}
                                  onChange={(e) => handleChange('hero', 'cta_primary_link', e.target.value)}
                                  placeholder="/eventos"
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  Botón Secundario - Texto
                                </Label>
                                <input
                                  value={settings.hero.cta_secondary_text}
                                  onChange={(e) => handleChange('hero', 'cta_secondary_text', e.target.value)}
                                  placeholder="Galería"
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  Botón Secundario - Link
                                </Label>
                                <input
                                  value={settings.hero.cta_secondary_link}
                                  onChange={(e) => handleChange('hero', 'cta_secondary_link', e.target.value)}
                                  placeholder="/galeria"
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                Imagen de Fondo (URL)
                              </Label>
                              <input
                                value={settings.hero.background_image}
                                onChange={(e) => handleChange('hero', 'background_image', e.target.value)}
                                placeholder="https://images.unsplash.com/..."
                                className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                              />
                              {settings.hero.background_image && (
                                <div className="mt-4 relative aspect-video max-w-md overflow-hidden border border-[#27272A]">
                                  <img 
                                    src={settings.hero.background_image} 
                                    alt="Preview" 
                                    className="w-full h-full object-cover opacity-60"
                                  />
                                  <span className="absolute bottom-2 left-2 text-xs text-[#A1A1AA] bg-black/50 px-2 py-1">
                                    Preview
                                  </span>
                                </div>
                              )}
                            </div>

                            <VideoUploadField 
                              value={settings.hero.background_video || ''}
                              onChange={(url) => handleChange('hero', 'background_video', url)}
                            />
                          </div>
                        </div>

                        {/* Hero Social Links */}
                        <div className="admin-card">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold uppercase tracking-tight">
                              Redes Sociales (Hero)
                            </h3>
                            <Button
                              onClick={() => addSocialLink('hero')}
                              size="sm"
                              className="bg-[#27272A] hover:bg-[#3F3F46] text-white"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                          
                          {settings.hero.social_links?.length === 0 ? (
                            <p className="text-[#52525B] text-sm">No hay redes sociales configuradas</p>
                          ) : (
                            <div className="space-y-4">
                              {settings.hero.social_links?.map((link, index) => {
                                const SocialIcon = socialIcons[link.platform] || ExternalLink;
                                return (
                                  <div key={index} className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#27272A]">
                                    <SocialIcon className="w-5 h-5 text-[#A1A1AA]" />
                                    <select
                                      value={link.platform}
                                      onChange={(e) => handleSocialChange('hero', index, 'platform', e.target.value)}
                                      className="bg-transparent border-b border-[#27272A] py-2 text-white outline-none"
                                    >
                                      <option value="instagram">Instagram</option>
                                      <option value="twitter">Twitter/X</option>
                                      <option value="youtube">YouTube</option>
                                      <option value="facebook">Facebook</option>
                                      <option value="tiktok">TikTok</option>
                                      <option value="linkedin">LinkedIn</option>
                                      <option value="whatsapp">WhatsApp</option>
                                      <option value="email">Email</option>
                                    </select>
                                    <input
                                      value={link.url}
                                      onChange={(e) => handleSocialChange('hero', index, 'url', e.target.value)}
                                      placeholder={link.platform === 'email' ? 'mailto:correo@ejemplo.com' : link.platform === 'whatsapp' ? 'https://wa.me/5491112345678' : 'https://...'}
                                      className="flex-1 bg-transparent border-b border-[#27272A] focus:border-white py-2 text-white placeholder:text-[#52525B] outline-none"
                                    />
                                    <Switch
                                      checked={link.enabled}
                                      onCheckedChange={(checked) => handleSocialChange('hero', index, 'enabled', checked)}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeSocialLink('hero', index)}
                                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                    >
                                      <Trash2 size={16} />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* STATS Section */}
                    {activeSection === 'stats' && (
                      <motion.div
                        key="stats"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="admin-card"
                      >
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h2 className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
                              <BarChart3 size={20} />
                              Estadísticas
                            </h2>
                            <p className="text-[#A1A1AA] text-sm mt-1">
                              Los números que se muestran en la sección de estadísticas
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm text-[#A1A1AA]">Mostrar sección</Label>
                              <Switch
                                checked={settings.stats.enabled}
                                onCheckedChange={(checked) => handleChange('stats', 'enabled', checked)}
                              />
                            </div>
                            <Button
                              onClick={addStat}
                              size="sm"
                              className="bg-[#27272A] hover:bg-[#3F3F46] text-white"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-4">
                          {settings.stats.items?.map((stat, index) => {
                            const IconComponent = iconMap[stat.icon] || Sparkles;
                            return (
                              <div 
                                key={index} 
                                className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#27272A]"
                              >
                                <GripVertical size={18} className="text-[#52525B] cursor-move" />
                                <div className="w-10 h-10 bg-[#1A1A1A] flex items-center justify-center">
                                  <IconComponent size={20} className="text-white" />
                                </div>
                                <div className="flex-1 grid grid-cols-3 gap-4">
                                  <div>
                                    <Label className="text-xs text-[#52525B] mb-1 block">Valor</Label>
                                    <input
                                      value={stat.value}
                                      onChange={(e) => handleStatChange(index, 'value', e.target.value)}
                                      placeholder="50+"
                                      className="w-full bg-transparent border-b border-[#27272A] focus:border-white py-2 text-xl font-black text-white outline-none"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-[#52525B] mb-1 block">Etiqueta</Label>
                                    <input
                                      value={stat.label}
                                      onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                                      placeholder="Eventos"
                                      className="w-full bg-transparent border-b border-[#27272A] focus:border-white py-2 text-white outline-none"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-[#52525B] mb-1 block">Icono</Label>
                                    <select
                                      value={stat.icon}
                                      onChange={(e) => handleStatChange(index, 'icon', e.target.value)}
                                      className="w-full bg-transparent border-b border-[#27272A] py-2 text-white outline-none"
                                    >
                                      <option value="sparkles">Sparkles</option>
                                      <option value="music">Música</option>
                                      <option value="users">Usuarios</option>
                                      <option value="award">Premio</option>
                                    </select>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeStat(index)}
                                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            );
                          })}
                        </div>

                        {settings.stats.items?.length === 0 && (
                          <p className="text-center text-[#52525B] py-8">
                            No hay estadísticas configuradas. Haz clic en &quot;Agregar&quot; para crear una.
                          </p>
                        )}
                      </motion.div>
                    )}

                    {/* CTA Section */}
                    {activeSection === 'cta' && (
                      <motion.div
                        key="cta"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="admin-card"
                      >
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h2 className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
                              <MessageSquare size={20} />
                              Call to Action
                            </h2>
                            <p className="text-[#A1A1AA] text-sm mt-1">
                              La sección de llamada a la acción al final de la página
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm text-[#A1A1AA]">Mostrar sección</Label>
                            <Switch
                              checked={settings.cta.enabled}
                              onCheckedChange={(checked) => handleChange('cta', 'enabled', checked)}
                            />
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                              Título
                            </Label>
                            <input
                              value={settings.cta.title}
                              onChange={(e) => handleChange('cta', 'title', e.target.value)}
                              placeholder="¿Listo para la experiencia?"
                              className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-2xl font-black text-white placeholder:text-[#52525B] outline-none transition-colors"
                            />
                          </div>

                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                              Descripción
                            </Label>
                            <textarea
                              value={settings.cta.description}
                              onChange={(e) => handleChange('cta', 'description', e.target.value)}
                              placeholder="Descripción del CTA..."
                              rows={2}
                              className="w-full bg-transparent border border-[#27272A] focus:border-white p-4 text-white placeholder:text-[#52525B] outline-none transition-colors resize-none"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-6">
                            <div>
                              <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                Texto del Botón
                              </Label>
                              <input
                                value={settings.cta.button_text}
                                onChange={(e) => handleChange('cta', 'button_text', e.target.value)}
                                placeholder="Explorar Eventos"
                                className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                              />
                            </div>
                            <div>
                              <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                Link del Botón
                              </Label>
                              <input
                                value={settings.cta.button_link}
                                onChange={(e) => handleChange('cta', 'button_link', e.target.value)}
                                placeholder="/eventos"
                                className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* FOOTER Section */}
                    {activeSection === 'footer' && (
                      <motion.div
                        key="footer"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="admin-card">
                          <h2 className="text-xl font-black uppercase tracking-tight mb-6 flex items-center gap-2">
                            <Type size={20} />
                            Footer - Información de Contacto
                          </h2>

                          <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  <Mail size={14} className="inline mr-2" />
                                  Email de Contacto
                                </Label>
                                <input
                                  value={settings.footer.contact_email}
                                  onChange={(e) => handleChange('footer', 'contact_email', e.target.value)}
                                  placeholder="info@empresa.com"
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  <Phone size={14} className="inline mr-2" />
                                  Teléfono
                                </Label>
                                <input
                                  value={settings.footer.contact_phone}
                                  onChange={(e) => handleChange('footer', 'contact_phone', e.target.value)}
                                  placeholder="+54 11 1234-5678"
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                Texto de Copyright
                              </Label>
                              <input
                                value={settings.footer.copyright_text}
                                onChange={(e) => handleChange('footer', 'copyright_text', e.target.value)}
                                placeholder="Bravo Producciones. Todos los derechos reservados."
                                className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Footer Social Links */}
                        <div className="admin-card">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold uppercase tracking-tight">
                              Redes Sociales (Footer)
                            </h3>
                            <Button
                              onClick={() => addSocialLink('footer')}
                              size="sm"
                              className="bg-[#27272A] hover:bg-[#3F3F46] text-white"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                          
                          {settings.footer.social_links?.length === 0 ? (
                            <p className="text-[#52525B] text-sm">No hay redes sociales configuradas</p>
                          ) : (
                            <div className="space-y-4">
                              {settings.footer.social_links?.map((link, index) => {
                                const SocialIcon = socialIcons[link.platform] || ExternalLink;
                                return (
                                  <div key={index} className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#27272A]">
                                    <SocialIcon className="w-5 h-5 text-[#A1A1AA]" />
                                    <select
                                      value={link.platform}
                                      onChange={(e) => handleSocialChange('footer', index, 'platform', e.target.value)}
                                      className="bg-transparent border-b border-[#27272A] py-2 text-white outline-none"
                                    >
                                      <option value="instagram">Instagram</option>
                                      <option value="twitter">Twitter/X</option>
                                      <option value="youtube">YouTube</option>
                                      <option value="facebook">Facebook</option>
                                      <option value="tiktok">TikTok</option>
                                    </select>
                                    <input
                                      value={link.url}
                                      onChange={(e) => handleSocialChange('footer', index, 'url', e.target.value)}
                                      placeholder="https://..."
                                      className="flex-1 bg-transparent border-b border-[#27272A] focus:border-white py-2 text-white placeholder:text-[#52525B] outline-none"
                                    />
                                    <Switch
                                      checked={link.enabled}
                                      onCheckedChange={(checked) => handleSocialChange('footer', index, 'enabled', checked)}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeSocialLink('footer', index)}
                                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                    >
                                      <Trash2 size={16} />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Gallery Section */}
                    {activeSection === 'gallery' && (
                      <motion.div
                        key="gallery"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="admin-card">
                          <h2 className="text-xl font-black uppercase tracking-tight mb-6 flex items-center gap-2">
                            <HardDrive size={20} />
                            Google Drive
                          </h2>

                          <div className="bg-blue-500/10 border border-blue-500/30 p-4 mb-6">
                            <p className="text-blue-400 text-sm">
                              Conecta una carpeta de Google Drive para mostrar fotos y videos automáticamente en la galería pública.
                              La carpeta debe estar configurada como <strong>&quot;Cualquier persona con el enlace&quot;</strong> puede ver.
                            </p>
                          </div>
                          
                          <div className="space-y-6">
                            <div>
                              <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                ID de Carpeta de Google Drive
                              </Label>
                              <input
                                value={settings.gallery?.drive_folder_id || ''}
                                onChange={(e) => handleChange('gallery', 'drive_folder_id', e.target.value)}
                                placeholder="1ABC123def456..."
                                className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors font-mono"
                              />
                              <p className="text-[#52525B] text-xs mt-2">
                                Puedes encontrar el ID en la URL de la carpeta: drive.google.com/drive/folders/<strong>ID_AQUÍ</strong>
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  Título de la Galería
                                </Label>
                                <input
                                  value={settings.gallery?.title || ''}
                                  onChange={(e) => handleChange('gallery', 'title', e.target.value)}
                                  placeholder="Galería"
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                              <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                  Descripción
                                </Label>
                                <input
                                  value={settings.gallery?.description || ''}
                                  onChange={(e) => handleChange('gallery', 'description', e.target.value)}
                                  placeholder="Revive los mejores momentos..."
                                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                />
                              </div>
                            </div>

                            {/* Premium Download Price */}
                            <div className="border-t border-[#27272A] pt-6 mt-6">
                              <h3 className="text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                                <Award size={18} className="text-yellow-500" />
                                Descarga Premium
                              </h3>
                              <div className="grid grid-cols-2 gap-6">
                                <div>
                                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                                    Precio por Descarga (ARS)
                                  </Label>
                                  <div className="flex items-center">
                                    <span className="text-[#52525B] mr-2">$</span>
                                    <input
                                      type="number"
                                      value={settings.gallery?.premium_download_price || 500}
                                      onChange={(e) => handleChange('gallery', 'premium_download_price', parseInt(e.target.value) || 0)}
                                      placeholder="500"
                                      min="0"
                                      step="50"
                                      className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white placeholder:text-[#52525B] outline-none transition-colors"
                                    />
                                  </div>
                                  <p className="text-[#52525B] text-xs mt-2">
                                    Precio que los usuarios pagarán para descargar fotos sin marca de agua en calidad original
                                  </p>
                                </div>
                                <div className="flex items-center">
                                  <div className="bg-[#1A1A1A] border border-[#27272A] p-4 w-full">
                                    <p className="text-xs text-[#A1A1AA] uppercase tracking-widest mb-2">Vista previa</p>
                                    <p className="text-2xl font-black">${settings.gallery?.premium_download_price || 500} <span className="text-sm font-normal text-[#A1A1AA]">ARS</span></p>
                                    <p className="text-xs text-[#52525B] mt-1">por foto sin marca de agua</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Help Card */}
                        <div className="admin-card bg-[#0A0A0A]">
                          <h3 className="text-lg font-bold uppercase tracking-tight mb-4">
                            ¿Cómo configurar?
                          </h3>
                          <ol className="space-y-3 text-[#A1A1AA] text-sm">
                            <li className="flex gap-3">
                              <span className="w-6 h-6 bg-[#27272A] flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                              <span>Abre Google Drive y ve a la carpeta que contiene tus fotos/videos</span>
                            </li>
                            <li className="flex gap-3">
                              <span className="w-6 h-6 bg-[#27272A] flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                              <span>Haz clic derecho en la carpeta → <strong>Compartir</strong></span>
                            </li>
                            <li className="flex gap-3">
                              <span className="w-6 h-6 bg-[#27272A] flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                              <span>En &quot;Acceso general&quot;, selecciona <strong>&quot;Cualquier persona con el enlace&quot;</strong></span>
                            </li>
                            <li className="flex gap-3">
                              <span className="w-6 h-6 bg-[#27272A] flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                              <span>Copia el ID de la URL: drive.google.com/drive/folders/<strong>ESTE_ES_EL_ID</strong></span>
                            </li>
                            <li className="flex gap-3">
                              <span className="w-6 h-6 bg-[#27272A] flex items-center justify-center flex-shrink-0 text-xs font-bold">5</span>
                              <span>Pega el ID arriba y guarda los cambios</span>
                            </li>
                          </ol>
                        </div>
                      </motion.div>
                    )}

                    {/* FULL BRAVX Section */}
                    {activeSection === 'fullbravx' && (
                      <motion.div
                        key="fullbravx"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        {/* Header Card */}
                        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-xl p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                                <Crown size={24} className="text-[#D4AF37]" />
                              </div>
                              <div>
                                <h2 className="text-xl font-black text-white">FULL BRAVX</h2>
                                <p className="text-[#A1A1AA] text-sm">Sección de membresía VIP en el homepage</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 bg-[#1A1A1A] rounded-lg px-4 py-2">
                              <span className="text-sm text-[#A1A1AA]">Visible</span>
                              <Switch
                                checked={settings.fullbravx?.enabled !== false}
                                onCheckedChange={(checked) => handleChange('fullbravx', 'enabled', checked)}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Content Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A]">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Contenido Principal</h3>
                          </div>
                          <div className="p-6 space-y-5">
                            <div className="grid md:grid-cols-2 gap-5">
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Badge / Etiqueta</label>
                                <input
                                  value={settings.fullbravx?.badge || ''}
                                  onChange={(e) => handleChange('fullbravx', 'badge', e.target.value)}
                                  placeholder="Comunidad VIP"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Título Principal</label>
                                <input
                                  value={settings.fullbravx?.title || ''}
                                  onChange={(e) => handleChange('fullbravx', 'title', e.target.value)}
                                  placeholder="FULL BRAVX"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-[#71717A]">Descripción</label>
                              <textarea
                                value={settings.fullbravx?.description || ''}
                                onChange={(e) => handleChange('fullbravx', 'description', e.target.value)}
                                placeholder="Más que una suscripción, es pertenecer a una comunidad exclusiva..."
                                rows={3}
                                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all resize-none"
                              />
                            </div>
                          </div>
                        </div>

                        {/* CTA Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A]">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Botón de Acción (CTA)</h3>
                          </div>
                          <div className="p-6 space-y-5">
                            <div className="grid md:grid-cols-2 gap-5">
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Texto del Botón</label>
                                <input
                                  value={settings.fullbravx?.cta_text || ''}
                                  onChange={(e) => handleChange('fullbravx', 'cta_text', e.target.value)}
                                  placeholder="Unirme a FULL BRAVX"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Link del Botón</label>
                                <input
                                  value={settings.fullbravx?.cta_link || ''}
                                  onChange={(e) => handleChange('fullbravx', 'cta_link', e.target.value)}
                                  placeholder="/fullbravx"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-[#71717A]">Subtítulo bajo el botón</label>
                              <input
                                value={settings.fullbravx?.cta_subtitle || ''}
                                onChange={(e) => handleChange('fullbravx', 'cta_subtitle', e.target.value)}
                                placeholder="Probá 15 días gratis • Cancelá cuando quieras"
                                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Benefits Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A] flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Beneficios</h3>
                              <p className="text-xs text-[#52525B] mt-1">Máximo 6 beneficios que se mostrarán en el grid</p>
                            </div>
                            <Button
                              onClick={() => {
                                const benefits = settings.fullbravx?.benefits || [];
                                if (benefits.length < 6) {
                                  handleChange('fullbravx', 'benefits', [...benefits, { icon: 'gift', title: '', description: '' }]);
                                }
                              }}
                              disabled={(settings.fullbravx?.benefits || []).length >= 6}
                              size="sm"
                              className="bg-[#D4AF37] hover:bg-[#D4AF37]/80 text-black font-medium"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                          <div className="p-6">
                            {(settings.fullbravx?.benefits || []).length === 0 ? (
                              <div className="text-center py-8 text-[#52525B]">
                                <Gift size={32} className="mx-auto mb-2 opacity-50" />
                                <p>No hay beneficios configurados</p>
                                <p className="text-xs mt-1">Agregá beneficios para mostrar en la sección</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {(settings.fullbravx?.benefits || []).map((benefit, index) => (
                                  <div key={index} className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-[#27272A] rounded-lg group hover:border-[#D4AF37]/30 transition-colors">
                                    <select
                                      value={benefit.icon}
                                      onChange={(e) => {
                                        const newBenefits = [...(settings.fullbravx?.benefits || [])];
                                        newBenefits[index] = { ...newBenefits[index], icon: e.target.value };
                                        handleChange('fullbravx', 'benefits', newBenefits);
                                      }}
                                      className="bg-[#1A1A1A] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none"
                                    >
                                      <option value="gift">🎁 Regalo</option>
                                      <option value="ticket">🎫 Ticket</option>
                                      <option value="star">⭐ Estrella</option>
                                      <option value="heart">❤️ Corazón</option>
                                      <option value="zap">⚡ Rayo</option>
                                      <option value="shield">🛡️ Escudo</option>
                                    </select>
                                    <input
                                      value={benefit.title}
                                      onChange={(e) => {
                                        const newBenefits = [...(settings.fullbravx?.benefits || [])];
                                        newBenefits[index] = { ...newBenefits[index], title: e.target.value };
                                        handleChange('fullbravx', 'benefits', newBenefits);
                                      }}
                                      placeholder="Título del beneficio"
                                      className="flex-1 bg-transparent border-0 text-white placeholder:text-[#52525B] outline-none"
                                    />
                                    <input
                                      value={benefit.description}
                                      onChange={(e) => {
                                        const newBenefits = [...(settings.fullbravx?.benefits || [])];
                                        newBenefits[index] = { ...newBenefits[index], description: e.target.value };
                                        handleChange('fullbravx', 'benefits', newBenefits);
                                      }}
                                      placeholder="Descripción breve"
                                      className="flex-1 bg-transparent border-0 text-[#A1A1AA] placeholder:text-[#52525B] outline-none text-sm"
                                    />
                                    <button
                                      onClick={() => {
                                        const newBenefits = (settings.fullbravx?.benefits || []).filter((_, i) => i !== index);
                                        handleChange('fullbravx', 'benefits', newBenefits);
                                      }}
                                      className="p-2 text-[#52525B] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* PRODUCTORAS Section */}
                    {activeSection === 'productoras' && (
                      <motion.div
                        key="productoras"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        {/* Header Card */}
                        <div className="bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/20 rounded-xl p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                                <Building2 size={24} className="text-purple-400" />
                              </div>
                              <div>
                                <h2 className="text-xl font-black text-white">Productoras</h2>
                                <p className="text-[#A1A1AA] text-sm">Sección para atraer productoras de eventos</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 bg-[#1A1A1A] rounded-lg px-4 py-2">
                              <span className="text-sm text-[#A1A1AA]">Visible</span>
                              <Switch
                                checked={settings.productoras?.enabled !== false}
                                onCheckedChange={(checked) => handleChange('productoras', 'enabled', checked)}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Content Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A]">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Contenido Principal</h3>
                          </div>
                          <div className="p-6 space-y-5">
                            <div className="grid md:grid-cols-2 gap-5">
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Badge / Etiqueta</label>
                                <input
                                  value={settings.productoras?.badge || ''}
                                  onChange={(e) => handleChange('productoras', 'badge', e.target.value)}
                                  placeholder="Para Productoras"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none transition-all"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Título (Línea 1)</label>
                                <input
                                  value={settings.productoras?.title || ''}
                                  onChange={(e) => handleChange('productoras', 'title', e.target.value)}
                                  placeholder="Tu plataforma para"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none transition-all"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-[#71717A]">Título Destacado (Línea 2 - en color púrpura)</label>
                              <input
                                value={settings.productoras?.title_highlight || ''}
                                onChange={(e) => handleChange('productoras', 'title_highlight', e.target.value)}
                                placeholder="crear eventos épicos"
                                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-purple-400 font-bold placeholder:text-[#52525B] focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none transition-all"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-[#71717A]">Descripción</label>
                              <textarea
                                value={settings.productoras?.description || ''}
                                onChange={(e) => handleChange('productoras', 'description', e.target.value)}
                                placeholder="Bravo te ofrece todas las herramientas que necesitás..."
                                rows={3}
                                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none transition-all resize-none"
                              />
                            </div>
                            <div className="grid md:grid-cols-2 gap-5">
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Texto del Botón</label>
                                <input
                                  value={settings.productoras?.cta_text || ''}
                                  onChange={(e) => handleChange('productoras', 'cta_text', e.target.value)}
                                  placeholder="Quiero ser productora"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none transition-all"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Link del Botón</label>
                                <input
                                  value={settings.productoras?.cta_link || ''}
                                  onChange={(e) => handleChange('productoras', 'cta_link', e.target.value)}
                                  placeholder="/productoras/aplicar"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none transition-all"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Features Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A] flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Características</h3>
                              <p className="text-xs text-[#52525B] mt-1">Lista de features que se mostrarán (máx. 4)</p>
                            </div>
                            <Button
                              onClick={() => {
                                const features = settings.productoras?.features || [];
                                if (features.length < 4) {
                                  handleChange('productoras', 'features', [...features, { icon: 'trending-up', text: '' }]);
                                }
                              }}
                              disabled={(settings.productoras?.features || []).length >= 4}
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-500 text-white font-medium"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                          <div className="p-6">
                            {(settings.productoras?.features || []).length === 0 ? (
                              <div className="text-center py-8 text-[#52525B]">
                                <TrendingUp size={32} className="mx-auto mb-2 opacity-50" />
                                <p>No hay características configuradas</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {(settings.productoras?.features || []).map((feature, index) => (
                                  <div key={index} className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-[#27272A] rounded-lg hover:border-purple-500/30 transition-colors">
                                    <select
                                      value={feature.icon}
                                      onChange={(e) => {
                                        const newFeatures = [...(settings.productoras?.features || [])];
                                        newFeatures[index] = { ...newFeatures[index], icon: e.target.value };
                                        handleChange('productoras', 'features', newFeatures);
                                      }}
                                      className="bg-[#1A1A1A] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none"
                                    >
                                      <option value="trending-up">📈 Gráfico</option>
                                      <option value="ticket">🎫 Ticket</option>
                                      <option value="users">👥 Usuarios</option>
                                      <option value="shield">🛡️ Escudo</option>
                                      <option value="zap">⚡ Rayo</option>
                                    </select>
                                    <input
                                      value={feature.text}
                                      onChange={(e) => {
                                        const newFeatures = [...(settings.productoras?.features || [])];
                                        newFeatures[index] = { ...newFeatures[index], text: e.target.value };
                                        handleChange('productoras', 'features', newFeatures);
                                      }}
                                      placeholder="Descripción de la característica"
                                      className="flex-1 bg-transparent border-0 text-white placeholder:text-[#52525B] outline-none"
                                    />
                                    <button
                                      onClick={() => {
                                        const newFeatures = (settings.productoras?.features || []).filter((_, i) => i !== index);
                                        handleChange('productoras', 'features', newFeatures);
                                      }}
                                      className="p-2 text-[#52525B] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Dashboard Stats Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A] flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Estadísticas del Dashboard</h3>
                              <p className="text-xs text-[#52525B] mt-1">Datos que se mostrarán en el mockup (máx. 4)</p>
                            </div>
                            <Button
                              onClick={() => {
                                const stats = settings.productoras?.stats || [];
                                if (stats.length < 4) {
                                  handleChange('productoras', 'stats', [...stats, { label: '', value: '', color: 'purple' }]);
                                }
                              }}
                              disabled={(settings.productoras?.stats || []).length >= 4}
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-500 text-white font-medium"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                          <div className="p-6">
                            {(settings.productoras?.stats || []).length === 0 ? (
                              <div className="text-center py-8 text-[#52525B]">
                                <BarChart3 size={32} className="mx-auto mb-2 opacity-50" />
                                <p>No hay estadísticas configuradas</p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-3">
                                {(settings.productoras?.stats || []).map((stat, index) => (
                                  <div key={index} className="p-4 bg-[#0A0A0A] border border-[#27272A] rounded-lg hover:border-purple-500/30 transition-colors">
                                    <div className="flex items-center justify-between mb-3">
                                      <select
                                        value={stat.color}
                                        onChange={(e) => {
                                          const newStats = [...(settings.productoras?.stats || [])];
                                          newStats[index] = { ...newStats[index], color: e.target.value };
                                          handleChange('productoras', 'stats', newStats);
                                        }}
                                        className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1 text-xs text-white outline-none"
                                      >
                                        <option value="purple">🟣 Púrpura</option>
                                        <option value="green">🟢 Verde</option>
                                        <option value="blue">🔵 Azul</option>
                                        <option value="orange">🟠 Naranja</option>
                                      </select>
                                      <button
                                        onClick={() => {
                                          const newStats = (settings.productoras?.stats || []).filter((_, i) => i !== index);
                                          handleChange('productoras', 'stats', newStats);
                                        }}
                                        className="p-1 text-[#52525B] hover:text-red-400"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                    <input
                                      value={stat.value}
                                      onChange={(e) => {
                                        const newStats = [...(settings.productoras?.stats || [])];
                                        newStats[index] = { ...newStats[index], value: e.target.value };
                                        handleChange('productoras', 'stats', newStats);
                                      }}
                                      placeholder="1,247"
                                      className="w-full bg-transparent text-2xl font-black text-purple-400 placeholder:text-[#52525B] outline-none mb-1"
                                    />
                                    <input
                                      value={stat.label}
                                      onChange={(e) => {
                                        const newStats = [...(settings.productoras?.stats || [])];
                                        newStats[index] = { ...newStats[index], label: e.target.value };
                                        handleChange('productoras', 'stats', newStats);
                                      }}
                                      placeholder="Entradas vendidas"
                                      className="w-full bg-transparent text-xs text-[#52525B] placeholder:text-[#3F3F46] outline-none"
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* PARTNERS Section */}
                    {activeSection === 'partners' && (
                      <motion.div
                        key="partners"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        {/* Header Card */}
                        <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-xl p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                                <Handshake size={24} className="text-emerald-400" />
                              </div>
                              <div>
                                <h2 className="text-xl font-black text-white">Partners</h2>
                                <p className="text-[#A1A1AA] text-sm">Sección para atraer negocios aliados</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 bg-[#1A1A1A] rounded-lg px-4 py-2">
                              <span className="text-sm text-[#A1A1AA]">Visible</span>
                              <Switch
                                checked={settings.partners?.enabled !== false}
                                onCheckedChange={(checked) => handleChange('partners', 'enabled', checked)}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Content Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A]">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Contenido Principal</h3>
                          </div>
                          <div className="p-6 space-y-5">
                            <div className="grid md:grid-cols-2 gap-5">
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Badge / Etiqueta</label>
                                <input
                                  value={settings.partners?.badge || ''}
                                  onChange={(e) => handleChange('partners', 'badge', e.target.value)}
                                  placeholder="Para Partners"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Título (Línea 1)</label>
                                <input
                                  value={settings.partners?.title || ''}
                                  onChange={(e) => handleChange('partners', 'title', e.target.value)}
                                  placeholder="Conectá tu negocio con"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-[#71717A]">Título Destacado (Línea 2 - en color verde)</label>
                              <input
                                value={settings.partners?.title_highlight || ''}
                                onChange={(e) => handleChange('partners', 'title_highlight', e.target.value)}
                                placeholder="nuestra comunidad"
                                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-emerald-400 font-bold placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-[#71717A]">Descripción</label>
                              <textarea
                                value={settings.partners?.description || ''}
                                onChange={(e) => handleChange('partners', 'description', e.target.value)}
                                placeholder="Ofrecé beneficios exclusivos a los miembros de FULL BRAVX..."
                                rows={3}
                                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all resize-none"
                              />
                            </div>
                            <div className="grid md:grid-cols-2 gap-5">
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Texto del Botón</label>
                                <input
                                  value={settings.partners?.cta_text || ''}
                                  onChange={(e) => handleChange('partners', 'cta_text', e.target.value)}
                                  placeholder="Quiero ser partner"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Link del Botón</label>
                                <input
                                  value={settings.partners?.cta_link || ''}
                                  onChange={(e) => handleChange('partners', 'cta_link', e.target.value)}
                                  placeholder="/quiero-ser-partner"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all"
                                />
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-5">
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Estadística Principal</label>
                                <input
                                  value={settings.partners?.stats_label || ''}
                                  onChange={(e) => handleChange('partners', 'stats_label', e.target.value)}
                                  placeholder="+50 Partners"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-emerald-400 font-bold placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-[#71717A]">Subtítulo Estadística</label>
                                <input
                                  value={settings.partners?.stats_sublabel || ''}
                                  onChange={(e) => handleChange('partners', 'stats_sublabel', e.target.value)}
                                  placeholder="activos en la red"
                                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-all"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Features Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A] flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Características</h3>
                              <p className="text-xs text-[#52525B] mt-1">Lista de features (máx. 4)</p>
                            </div>
                            <Button
                              onClick={() => {
                                const features = settings.partners?.features || [];
                                if (features.length < 4) {
                                  handleChange('partners', 'features', [...features, { icon: 'users', text: '' }]);
                                }
                              }}
                              disabled={(settings.partners?.features || []).length >= 4}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                          <div className="p-6">
                            {(settings.partners?.features || []).length === 0 ? (
                              <div className="text-center py-8 text-[#52525B]">
                                <Users size={32} className="mx-auto mb-2 opacity-50" />
                                <p>No hay características configuradas</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {(settings.partners?.features || []).map((feature, index) => (
                                  <div key={index} className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-[#27272A] rounded-lg hover:border-emerald-500/30 transition-colors">
                                    <select
                                      value={feature.icon}
                                      onChange={(e) => {
                                        const newFeatures = [...(settings.partners?.features || [])];
                                        newFeatures[index] = { ...newFeatures[index], icon: e.target.value };
                                        handleChange('partners', 'features', newFeatures);
                                      }}
                                      className="bg-[#1A1A1A] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none"
                                    >
                                      <option value="users">👥 Usuarios</option>
                                      <option value="gift">🎁 Regalo</option>
                                      <option value="trending-up">📈 Gráfico</option>
                                      <option value="zap">⚡ Rayo</option>
                                      <option value="shield">🛡️ Escudo</option>
                                    </select>
                                    <input
                                      value={feature.text}
                                      onChange={(e) => {
                                        const newFeatures = [...(settings.partners?.features || [])];
                                        newFeatures[index] = { ...newFeatures[index], text: e.target.value };
                                        handleChange('partners', 'features', newFeatures);
                                      }}
                                      placeholder="Descripción de la característica"
                                      className="flex-1 bg-transparent border-0 text-white placeholder:text-[#52525B] outline-none"
                                    />
                                    <button
                                      onClick={() => {
                                        const newFeatures = (settings.partners?.features || []).filter((_, i) => i !== index);
                                        handleChange('partners', 'features', newFeatures);
                                      }}
                                      className="p-2 text-[#52525B] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Example Partners Card */}
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A] flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA]">Partners de Ejemplo</h3>
                              <p className="text-xs text-[#52525B] mt-1">Se mostrarán como tarjetas en la sección (máx. 3)</p>
                            </div>
                            <Button
                              onClick={() => {
                                const partners = settings.partners?.example_partners || [];
                                if (partners.length < 3) {
                                  handleChange('partners', 'example_partners', [...partners, { title: '', discount: '', category: '' }]);
                                }
                              }}
                              disabled={(settings.partners?.example_partners || []).length >= 3}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
                            >
                              <Plus size={16} className="mr-1" />
                              Agregar
                            </Button>
                          </div>
                          <div className="p-6">
                            {(settings.partners?.example_partners || []).length === 0 ? (
                              <div className="text-center py-8 text-[#52525B]">
                                <Gift size={32} className="mx-auto mb-2 opacity-50" />
                                <p>No hay partners de ejemplo</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {(settings.partners?.example_partners || []).map((partner, index) => (
                                  <div key={index} className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#27272A] rounded-lg hover:border-emerald-500/30 transition-colors">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
                                      <Gift size={20} className="text-emerald-400" />
                                    </div>
                                    <input
                                      value={partner.title}
                                      onChange={(e) => {
                                        const newPartners = [...(settings.partners?.example_partners || [])];
                                        newPartners[index] = { ...newPartners[index], title: e.target.value };
                                        handleChange('partners', 'example_partners', newPartners);
                                      }}
                                      placeholder="Nombre del negocio"
                                      className="flex-1 bg-transparent border-0 text-white font-medium placeholder:text-[#52525B] outline-none"
                                    />
                                    <input
                                      value={partner.discount}
                                      onChange={(e) => {
                                        const newPartners = [...(settings.partners?.example_partners || [])];
                                        newPartners[index] = { ...newPartners[index], discount: e.target.value };
                                        handleChange('partners', 'example_partners', newPartners);
                                      }}
                                      placeholder="20% OFF"
                                      className="w-24 bg-[#1A1A1A] border border-emerald-500/30 rounded-lg px-3 py-2 text-emerald-400 font-bold text-center placeholder:text-[#52525B] outline-none"
                                    />
                                    <input
                                      value={partner.category}
                                      onChange={(e) => {
                                        const newPartners = [...(settings.partners?.example_partners || [])];
                                        newPartners[index] = { ...newPartners[index], category: e.target.value };
                                        handleChange('partners', 'example_partners', newPartners);
                                      }}
                                      placeholder="Categoría"
                                      className="w-28 bg-transparent border-0 text-[#71717A] text-sm placeholder:text-[#52525B] outline-none"
                                    />
                                    <button
                                      onClick={() => {
                                        const newPartners = (settings.partners?.example_partners || []).filter((_, i) => i !== index);
                                        handleChange('partners', 'example_partners', newPartners);
                                      }}
                                      className="p-2 text-[#52525B] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeSection === 'mantenimiento' && (
                      <motion.div
                        key="mantenimiento"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-6"
                      >
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A]">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA] flex items-center gap-2">
                              <Ticket size={16} className="text-[#D4AF37]" />
                              Reparar Entradas y Órdenes
                            </h3>
                            <p className="text-xs text-[#52525B] mt-2 leading-relaxed">
                              Corrige datos históricos: restaura órdenes aprobadas que fueron marcadas como rechazadas por error,
                              normaliza emails de compradores (mayúsculas/minúsculas) y vincula entradas a las cuentas de usuario
                              para que aparezcan en "Mis Entradas".
                            </p>
                          </div>
                          <div className="p-6 space-y-4">
                            <Button
                              onClick={handleRepairTickets}
                              disabled={repairing}
                              className="bg-[#D4AF37] hover:bg-[#C09E2E] text-black font-bold"
                              data-testid="repair-tickets-button"
                            >
                              {repairing ? (
                                <><Loader2 size={16} className="mr-2 animate-spin" /> Reparando...</>
                              ) : (
                                <><Wrench size={16} className="mr-2" /> Ejecutar Reparación</>
                              )}
                            </Button>
                            {repairResult && (
                              <div className="bg-[#0A0A0A] border border-emerald-500/30 rounded-lg p-4 space-y-1" data-testid="repair-result">
                                <p className="text-sm text-emerald-400 font-bold flex items-center gap-2">
                                  <CheckCircle size={14} /> Reparación completada
                                </p>
                                <p className="text-xs text-[#A1A1AA]">Órdenes restauradas a aprobadas: <span className="text-white font-bold">{repairResult.orders_restored}</span></p>
                                <p className="text-xs text-[#A1A1AA]">Emails normalizados (órdenes): <span className="text-white font-bold">{repairResult.emails_normalized_orders}</span></p>
                                <p className="text-xs text-[#A1A1AA]">Emails normalizados (entradas): <span className="text-white font-bold">{repairResult.emails_normalized_tickets}</span></p>
                                <p className="text-xs text-[#A1A1AA]">Órdenes vinculadas a usuarios: <span className="text-white font-bold">{repairResult.user_ids_linked_orders}</span></p>
                                <p className="text-xs text-[#A1A1AA]">Entradas vinculadas a usuarios: <span className="text-white font-bold">{repairResult.user_ids_linked_tickets}</span></p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                          <div className="p-6 border-b border-[#27272A]">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-[#A1A1AA] flex items-center gap-2">
                              <Music size={16} className="text-[#D4AF37]" />
                              Reenviar Emails de Aprobación DJ / Agencias
                            </h3>
                            <p className="text-xs text-[#52525B] mt-2 leading-relaxed">
                              Envía el email de "perfil aprobado" a los DJs y agencias que fueron aprobados pero nunca
                              recibieron la notificación (perfiles sin email de cuenta vinculado). Es idempotente: no duplica envíos.
                              También corre automáticamente una vez después de cada deploy.
                            </p>
                          </div>
                          <div className="p-6 space-y-4">
                            <Button
                              onClick={handleResendDjEmails}
                              disabled={resendingDj}
                              className="bg-[#D4AF37] hover:bg-[#C09E2E] text-black font-bold"
                              data-testid="resend-dj-emails-button"
                            >
                              {resendingDj ? (
                                <><Loader2 size={16} className="mr-2 animate-spin" /> Enviando...</>
                              ) : (
                                <><Mail size={16} className="mr-2" /> Reenviar Emails Pendientes</>
                              )}
                            </Button>
                            {resendDjResult && (
                              <div className="bg-[#0A0A0A] border border-emerald-500/30 rounded-lg p-4 space-y-1" data-testid="resend-dj-result">
                                <p className="text-sm text-emerald-400 font-bold flex items-center gap-2">
                                  <CheckCircle size={14} /> Reenvío completado
                                </p>
                                <p className="text-xs text-[#A1A1AA]">Perfiles pendientes encontrados: <span className="text-white font-bold">{resendDjResult.eligible}</span></p>
                                <p className="text-xs text-[#A1A1AA]">Emails enviados: <span className="text-white font-bold">{resendDjResult.sent}</span></p>
                                <p className="text-xs text-[#A1A1AA]">Sin email disponible / error: <span className="text-white font-bold">{resendDjResult.skipped_no_email_or_error}</span></p>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default SettingsPage;
